// src/components/Navigation.js
// GR33D-SIMPLIFIED-V2: [2025-03-19] - Enhanced navigation avec support admin

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AUTHORIZED_VESTING_WALLETS } from '../constants/contract';
import Logger from '../utils/logger-adapter';

// Adresse du wallet admin
const ADMIN_ADDRESS = "0x5A46d0F5bbce72D9665689cDAe9993824260b882";

const Navigation = ({ account, isLoading }) => {
    const location = useLocation();
    const navRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    
    // Check if user is authorized for vesting
    const isAuthorizedForVesting = useMemo(() => {
        if (!account) return false;
        
        return Object.values(AUTHORIZED_VESTING_WALLETS)
            .map(addr => addr.toLowerCase())
            .includes(account.toLowerCase());
    }, [account]);
    
    // Check if user is admin
    const isAdmin = useMemo(() => {
        if (!account) return false;
        
        return account.toLowerCase() === ADMIN_ADDRESS.toLowerCase();
    }, [account]);

    // Navigation links including admin link if applicable
    const navLinks = useMemo(() => {
        const baseLinks = [
            { path: "/", label: "Home", requiredAuth: false },
            { path: "/dashboard", label: "Dashboard", requiredAuth: false },
            { path: "/about", label: "About Us", requiredAuth: false },
            { path: "/roadmap", label: "Roadmap", requiredAuth: false },
            { path: "/whitepaper", label: "Whitepaper", requiredAuth: false },
            { path: "/art", label: "Art Marketplace", requiredAuth: false },
            { path: "/staking", label: "Staking", requiredAuth: true },
            { path: "/liquidity", label: "Liquidity", requiredAuth: true }
        ];

        // Add admin link for admin wallet only
        if (isAdmin) {
            baseLinks.push({ 
                path: "/admin", 
                label: "Admin", 
                requiredAuth: true 
            });
        }

        // Add vesting link for authorized wallets
        if (isAuthorizedForVesting) {
            baseLinks.push({ 
                path: "/vesting", 
                label: "Vesting", 
                requiredAuth: true
            });
        }

        baseLinks.push({ path: "/contacts", label: "Contacts", requiredAuth: false });

        return baseLinks;
    }, [isAuthorizedForVesting, isAdmin]);
    
    // Check if navigation is scrollable
    useEffect(() => {
        const checkScrollable = () => {
            if (navRef.current) {
                const { scrollWidth, clientWidth } = navRef.current;
                setIsScrollable(scrollWidth > clientWidth);
            }
        };
        
        // Check initially and on resize
        checkScrollable();
        window.addEventListener('resize', checkScrollable);
        
        // Check again when account changes (which might change available links)
        const timer = setTimeout(checkScrollable, 100);
        
        return () => {
            window.removeEventListener('resize', checkScrollable);
            clearTimeout(timer);
        };
    }, [account, navLinks]);

    return (
        <div 
            className={`main-navigation ${isScrollable ? 'scrollable' : ''}`}
            ref={navRef}
        >
            {navLinks.map((link) => {
                // Only show protected links if authenticated
                const shouldShow = !link.requiredAuth || account;
                if (!shouldShow) return null;

                return (
                    <Link
                        key={link.path}
                        to={link.path}
                        className={location.pathname === link.path ? 'active' : ''}
                        onClick={() => {
                            Logger.log('navigation', 'Link clicked', {
                                path: link.path,
                                label: link.label
                            });
                        }}
                    >
                        {link.label}
                    </Link>
                );
            })}
            
            {/* Add instructions for mobile users if scrollable */}
            {isScrollable && (
                <span className="scroll-hint">
                    ⟺
                </span>
            )}
        </div>
    );
};

export default React.memo(Navigation);
